import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@bonusly/design-system';

import { useFetchRecipients } from './use-fetch-recipients';
import { useGenerateImageForAttachment } from './use-generate-image-for-attachment';
import { useGenerateVideoForAttachment } from './use-generate-video-for-attachment';
import { useValidationError } from './use-validation-error';

type SharedConfigParams = {
	includeSelf?: boolean;
	includeGroups?: boolean;
};

// @TODO reuse this hook on feedback
export const useSharedConfig = (params: SharedConfigParams = {}) => {
	const toast = useToast();
	const { t } = useTranslation('recognition');
	const [loadingFileInMemory, setLoadingFileInMemory] = useState(false);
	// you can include self when giving an award
	const include_self = !!params.includeSelf;
	// include groups is optional too
	const search_usergroups = !!params.includeGroups;
	const fetchMentions = useFetchRecipients({
		include_non_receivable: true,
		include_self,
		search_usergroups: false,
	});
	const fetchRecipients = useFetchRecipients({
		include_self,
		search_usergroups,
	});
	const { generateImageForAttachment, isLoading: isLoadingImage } = useGenerateImageForAttachment({
		onSettledCallback: () => setLoadingFileInMemory(false),
	});
	const {
		generateVideoForAttachment,
		isLoading: isLoadingVideo,
		cancelVideoGeneration,
	} = useGenerateVideoForAttachment({
		onSettledCallback: () => setLoadingFileInMemory(false),
	});

	const onUpload = (mediaFile: File, posterFile?: File | null): any => {
		const MAX_FILE_SIZE_IN_MB = 300;
		setLoadingFileInMemory(true);
		if (mediaFile.size > MAX_FILE_SIZE_IN_MB * 1024 * 1024) {
			setLoadingFileInMemory(false);
			toast({
				title: t('bonus_input.validation.file_size'),
				status: 'error',
			});
		} else {
			if (mediaFile.type.startsWith('video')) {
				return generateVideoForAttachment(mediaFile, posterFile);
			} else {
				return generateImageForAttachment(mediaFile);
			}
		}
	};
	const onValidationError = useValidationError();
	return {
		fetchMentions,
		fetchRecipients,
		onUpload,
		isLoadingAttachment: isLoadingImage || isLoadingVideo || loadingFileInMemory,
		cancelVideoGeneration,
		onValidationError,
	};
};
